import * as React from "react";
import { useState } from "react";
import { ToggleButton } from "@/components/ui/toggle-button";
import { SuggestedTopicsContent } from "./sections/suggested-topics";
import { MentorshipDetails } from "@/lib/api/mentorships";
import { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCcw } from "lucide-react";

type ContentType = "suggestedTopics" | "other";

interface SidebarUserInfoSectionsProps {
  title: string;
  content?: string;
  labels?: string[];
  showToggle?: boolean;
  defaultOpen?: boolean;
  lastUpdated?: string | Date;
  data?: any;
  type?: ContentType;
  tags?: ReactNode[];
  mentorship?: MentorshipDetails;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export const SidebarUserInfoSections = React.forwardRef<
  HTMLDivElement,
  SidebarUserInfoSectionsProps
>(
  (
    {
      title,
      content,
      labels,
      showToggle = true,
      defaultOpen = true,
      lastUpdated,
      data,
      type,
      tags,
      mentorship,
      onRefresh,
      isRefreshing,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    // Hide component if no content for non-suggested topics
    if (
      type !== "suggestedTopics" &&
      !content &&
      (!labels || labels.length === 0)
    ) {
      return null;
    }

    return (
      <div ref={ref} className="my-4 rounded-lg bg-white px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-twk font-weight-400 font-[14px] text-[#1C1C1C]">
              {title}
            </h3>
            {tags && tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => tag)}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {lastUpdated && (
              <span className="text-xs italic text-gray-500">
                Last updated {new Date(lastUpdated).toLocaleDateString()}
              </span>
            )}
            {type === "suggestedTopics" && onRefresh && (
              <Button
                variant="outline"
                className="m-2 rounded-full px-2 py-1 text-xs hover:text-blue-700"
                onClick={onRefresh}
                disabled={isRefreshing}
              >
                <RefreshCcw
                  className={`size-4 ${isRefreshing ? "animate-spin" : ""}`}
                />
              </Button>
            )}
            {showToggle && (
              <ToggleButton
                isOpen={isOpen}
                onToggle={() => setIsOpen(!isOpen)}
              />
            )}
          </div>
        </div>
        {isOpen && (
          <div className="mt-[8px]">
            {type === "suggestedTopics" ? (
              <SuggestedTopicsContent data={data} mentorship={mentorship} />
            ) : (
              <div className="flex flex-col gap-2">
                <span className="font-twk font-weight-400 mb-[8px] font-[12px] text-[#777777]">
                  {content}
                </span>
                <div className="flex flex-wrap gap-2">
                  {labels?.map((label) => (
                    <span
                      key={label}
                      className="font-twk font-weight-400 rounded-[100px] bg-[#F8F5EE] px-[12px] py-[4px] font-[12px] text-[#777777]"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);

SidebarUserInfoSections.displayName = "SidebarUserInfoSections";
